import React, { useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Camera, X } from 'lucide-react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import { updateEntry } from '../../services/dashboardApi';
import { compressImage } from '../../core/imageUtils';

const EditEntryModal = ({ isOpen, onClose, party, entry, onSuccess }) => {
  const { t } = useLang();
  
  const [currentEntryType, setCurrentEntryType] = useState(entry?.entry_type || 'get');
  const [amount, setAmount] = useState(entry?.amount?.toString() || '');
  const [note, setNote] = useState(entry?.note || '');
  
  // Safe date handling
  const getInitialDate = () => {
    if (!entry?.entry_date) return new Date().toISOString().split('T')[0];
    try {
      return entry.entry_date.split('T')[0];
    } catch (e) {
      return new Date().toISOString().split('T')[0];
    }
  };
  
  const [entryDate, setEntryDate] = useState(getInitialDate());
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(entry?.entry_image || null);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert(t(LangKeys.invalid_amount));
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('entry_type', currentEntryType);
      formData.append('amount', amount);
      formData.append('note', note);
      formData.append('entry_date', entryDate);
      if (selectedImage) {
        formData.append('entry_image', selectedImage);
      }

      const res = await updateEntry(entry.id, formData);
      if (res?.success) {
        onSuccess();
      } else {
        alert(res?.message || 'Update failed');
      }
    } catch (error) {
      alert('Network error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setSelectedImage(compressed);
        setPreviewImage(URL.createObjectURL(compressed));
      } catch (err) {
        console.error("Compression failed:", err);
        setSelectedImage(file);
        setPreviewImage(URL.createObjectURL(file));
      }
    }
  };

  const isGive = currentEntryType === 'give';
  const activeColor = isGive ? '#DC2626' : '#16A34A';

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* HEADER */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.closeBtn} onClick={onClose}>
              <ArrowLeft size={20} />
            </button>
            <h3 style={styles.title}>{t(LangKeys.edit_entry)}</h3>
          </div>
        </div>

        <div style={styles.scrollContent}>
          {/* TYPE TOGGLE */}
          <div style={styles.toggleRow}>
            <button 
              style={{
                ...styles.toggleBtn,
                backgroundColor: currentEntryType === 'get' ? '#F0FDF4' : 'transparent',
                borderColor: currentEntryType === 'get' ? '#16A34A' : '#E5E7EB',
                color: currentEntryType === 'get' ? '#16A34A' : '#64748B'
              }}
              onClick={() => setCurrentEntryType('get')}
            >
              {t(LangKeys.you_got)}
            </button>
            <button 
              style={{
                ...styles.toggleBtn,
                backgroundColor: currentEntryType === 'give' ? '#FEF2F2' : 'transparent',
                borderColor: currentEntryType === 'give' ? '#DC2626' : '#E5E7EB',
                color: currentEntryType === 'give' ? '#DC2626' : '#64748B'
              }}
              onClick={() => setCurrentEntryType('give')}
            >
              {t(LangKeys.you_gave)}
            </button>
          </div>

          {/* AMOUNT FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.amount)}</label>
            <div style={styles.amountInputWrap}>
              <span style={styles.currencyPrefix}>₹</span>
              <input 
                type="number" 
                style={styles.amountInput}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
                autoFocus
              />
            </div>
          </div>

          {/* DATE FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.date)}</label>
            <div style={styles.iconInputWrap}>
              <Calendar size={18} color="#94A3B8" />
              <input 
                type="date" 
                style={styles.textInput}
                value={entryDate}
                onChange={(e) => setEntryDate(e.target.value)}
              />
            </div>
          </div>

          {/* NOTE FIELD */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.note)}</label>
            <textarea 
              style={styles.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t(LangKeys.note_hint)}
            />
          </div>

          {/* PHOTO PICKER */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>{t(LangKeys.photo)}</label>
            <div style={styles.photoPicker} onClick={() => document.getElementById('edit-image-input').click()}>
              {previewImage ? (
                <img 
                  src={previewImage.startsWith('blob') || previewImage.startsWith('http') ? previewImage : `https://khata.moxpal.com/${previewImage}`} 
                  style={styles.preview} 
                  alt="Preview"
                />
              ) : (
                <div style={styles.photoPlaceholder}>
                  <Camera size={24} color="#94A3B8" />
                  <span>{t(LangKeys.add_photo_label)}</span>
                </div>
              )}
              <input 
                id="edit-image-input"
                type="file" 
                hidden 
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={styles.footer}>
          <button style={styles.cancelBtn} onClick={onClose}>{t(LangKeys.cancel)}</button>
          <button 
            style={{...styles.saveBtn, backgroundColor: activeColor}}
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? '...' : t(LangKeys.save)}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1001 },
  modal: { width: '100%', maxWidth: '500px', background: '#F8FAFC', borderRadius: '24px 24px 0 0', display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxShadow: '0 -10px 25px rgba(0,0,0,0.1)' },
  
  header: { padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', background: '#fff', borderRadius: '24px 24px 0 0' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  closeBtn: { background: '#F1F5F9', border: 'none', borderRadius: '10px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  title: { fontSize: '18px', fontWeight: 'bold', margin: 0 },
  
  scrollContent: { padding: '20px', overflowY: 'auto', flex: 1 },
  
  toggleRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px' },
  toggleBtn: { height: '48px', border: '2px solid', borderRadius: '12px', fontWeight: 'bold', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s' },
  
  inputGroup: { marginBottom: '20px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#64748B', marginBottom: '8px' },
  
  amountInputWrap: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '0 16px', height: '56px' },
  currencyPrefix: { fontSize: '20px', fontWeight: 'bold', color: '#1E293B', marginRight: '8px' },
  amountInput: { border: 'none', fontSize: '24px', fontWeight: 'bold', color: '#1E293B', width: '100%', outline: 'none' },
  
  iconInputWrap: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '12px', border: '1px solid #E2E8F0', padding: '0 16px', height: '52px', gap: '12px' },
  textInput: { border: 'none', fontSize: '15px', color: '#1E293B', width: '100%', outline: 'none', background: 'transparent' },
  
  textarea: { width: '100%', background: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '12px', fontSize: '15px', minHeight: '80px', outline: 'none', resize: 'none' },
  
  photoPicker: { width: '100%', height: '120px', background: '#fff', border: '1px dashed #CBD5E1', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden' },
  photoPlaceholder: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#94A3B8' },
  preview: { width: '100%', height: '100%', objectFit: 'cover' },
  
  footer: { padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#fff', borderTop: '1px solid #F1F5F9' },
  cancelBtn: { height: '50px', borderRadius: '12px', border: 'none', background: '#F1F5F9', color: '#475569', fontWeight: '700', cursor: 'pointer' },
  saveBtn: { height: '50px', borderRadius: '12px', border: 'none', color: '#fff', fontWeight: '700', cursor: 'pointer' }
};

export default EditEntryModal;
